import { Entity } from '@api/api/entity-schema';
import {
  CreateChartOptionsWithConfig,
  EventsConfig,
  HorizonWithPriceRange,
  RangeHorizon,
  SeriesType,
} from '@chart';
import { ChartV2Horizon } from '@chartv2';
import { Button } from '@design/components/button/Button';
import {
  ChartAreaWrapper,
  ChartAreaWrapperProps,
} from '@terminal/components/chart-area-wrapper/ChartAreaWrapper';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { appPaths } from '@terminal/routes/app-paths';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useChartV2Insights } from '@terminal/views/chart-v2/hooks/useChartV2Insights';
import {
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
  toChartV2Domain,
  toChartV2Horizon,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { ChartState } from '@terminal/views/turbo-chart/hooks/use-chart-state/useChartState';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { ChartAreaV2 } from './components/chart-area-v2/ChartAreaV2';
import { ChartHeader } from './components/chart-header/ChartHeader';
import { ChartLoading } from './components/chart-loading/ChartLoading';
import styles from './SimpleChart.module.scss';
import { getPriceHorizonSettings } from './utils/simple-chart-utils';

export interface SimpleChartProps {
  entity?: Entity;
  initialChartSettings?: ChartSettings;
  isChatMessage?: boolean;
  shouldShowChartHeader?: boolean;
  hasLineColourBasedOnPrice?: boolean;
  isWidget?: boolean;
  chartConfig?: ChartAreaWrapperProps['chartConfig'];
  isLegacyVariant?: boolean;
  onClickFullscreen?: (chartState: ChartState) => void;
  maxHorizon?: RangeHorizon;
  eventsConfig?: EventsConfig;
  showChartTypeSelector?: boolean;
  source: PosthogTrackingSource;
}

const DEFAULT_SIMPLE_CHART_CONFIG: Partial<
  CreateChartOptionsWithConfig['config']
> = {
  enableZoom: false,
  enableHover: true,
  lineGradient: [
    { stop: 0, opacity: 0.5 },
    { stop: 1, opacity: 0.1 },
  ],
  hoverVariant: 'simple',
};
const EXCLUDED_HORIZONS = [RangeHorizon.TwoWeeks, RangeHorizon.TwoMonths];

export const SimpleChart = ({
  entity,
  initialChartSettings,
  isChatMessage = false,
  hasLineColourBasedOnPrice = true,
  chartConfig,
  isLegacyVariant = true,
  shouldShowChartHeader = true,
  onClickFullscreen,
  maxHorizon,
  showChartTypeSelector = true,
  source,
  eventsConfig = {
    TMI: false,
  },
}: SimpleChartProps) => {
  const [horizonWithPriceRanges, setHorizonWithPriceRanges] = useState<
    HorizonWithPriceRange[]
  >([]);
  const { t } = useTranslation(['chart', 'analyze', 'countries', 'common']);
  const initialSeriesTypeRef = useRef<SeriesType | null>(null);
  const { colors } = useChartTheme();
  const location = useLocation();
  const {
    assetWithError,
    chartData,
    changeRange,
    changeSeriesType,
    horizonRanges,
    hasError,
    hasEmpty,
    disable1DHorizon,
    dailyTimeseries,
    chartState,
    retryInitialChartLoad,
    primaryAsset,
    primaryPane,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: !initialChartSettings,
  });

  const { horizon, resample, chartPanes, domain } = chartState;

  const finalChartConfig = {
    ...DEFAULT_SIMPLE_CHART_CONFIG,
    ...chartConfig,
  };

  // The colour-by-performance effect below mutates `chartData[0].lineColorToken`
  // in place (gold palette token -> bullish/bearish), which doesn't change the
  // `chartPanes` reference. Track the token explicitly so the series — and its
  // bullish/bearish gradient fill — recompute when the token flips.
  const primaryLineColorToken = chartData[0]?.lineColorToken;
  // biome-ignore lint/correctness/useExhaustiveDependencies: primaryLineColorToken is mutated in place on chartPanes[0].chartAssetData[0]; tracking it forces the series to recompute when the bullish/bearish token flips.
  const chartV2Series = useMemo(
    () =>
      terminalPanesToSeries(chartPanes, {
        shouldUseLineColorToken: hasLineColourBasedOnPrice,
        areaGradient: finalChartConfig.lineGradient,
        showLatestPrice: false,
      }).map(series => ({ ...series, yAxisLabel: '' })),
    [
      chartPanes,
      hasLineColourBasedOnPrice,
      finalChartConfig.lineGradient,
      primaryLineColorToken,
    ]
  );

  const availableHorizons = useMemo<ChartV2Horizon[]>(() => {
    const requestedMaxIndex = maxHorizon
      ? horizonRanges.findIndex(h => h === maxHorizon)
      : horizonRanges.length - 1;
    const maxHorizonIndex =
      requestedMaxIndex === -1 ? horizonRanges.length - 1 : requestedMaxIndex;
    const filtered = horizonRanges
      .slice(0, maxHorizonIndex + 1)
      .filter(range => !EXCLUDED_HORIZONS.includes(range));

    return filtered
      .map(toChartV2Horizon)
      .filter((value): value is ChartV2Horizon => Boolean(value));
  }, [horizonRanges, maxHorizon]);

  const chartV2Domain = useMemo(
    () => toChartV2Domain(domain, primaryAsset),
    [domain, primaryAsset]
  );

  const chartV2DefaultPriceDisplayByPane = useMemo(
    () => terminalPanesToDefaultPriceDisplayByPane(chartPanes),
    [chartPanes]
  );

  const chartV2Insights = useChartV2Insights({
    chartPanes,
    eventsConfig,
    small: true,
    source,
  });

  if (!initialSeriesTypeRef.current && chartPanes[0]?.seriesType) {
    initialSeriesTypeRef.current = chartPanes[0].seriesType;
  }

  const isAssetOverviewPage = location.pathname === appPaths.assetOverview;

  useEffect(() => {
    if (dailyTimeseries?.length && chartData[0]) {
      try {
        const { rangesWithPrice, lineColorToken } = getPriceHorizonSettings({
          horizonRanges,
          dailyTimeseries,
          horizon,
          colors,
        });

        if (hasLineColourBasedOnPrice && lineColorToken) {
          chartData[0].lineColorToken = lineColorToken;
        }

        setHorizonWithPriceRanges(rangesWithPrice);
      } catch (error) {
        console.error('Error processing chart timeseries data:', error);
        setHorizonWithPriceRanges([]);
      }
    }
  }, [dailyTimeseries]);

  useEffect(() => {
    if (horizonWithPriceRanges.length && !isAssetOverviewPage) {
      changeSeriesType(
        horizon === RangeHorizon.OneDay
          ? 'candlestick'
          : initialSeriesTypeRef.current!,
        {}
      );
    }
  }, [horizonWithPriceRanges, horizon]);

  const isLoading = !hasError && !hasEmpty && !dailyTimeseries && !chartData[0];

  if (isLoading) {
    return (
      <ChartLoading
        isLegacyVariant={isLegacyVariant}
        isChatMessage={isChatMessage}
      />
    );
  }

  const onRangeChange = (newHorizon: RangeHorizon) => {
    if (hasLineColourBasedOnPrice && chartData[0]) {
      const found = horizonWithPriceRanges.find(h => h.horizon === newHorizon);

      if (found) {
        chartData[0].lineColorToken = found.lineColorToken;
      }
    }

    changeRange(newHorizon);
  };

  const renderFullScreenButton = () => {
    if (!onClickFullscreen || !chartState.chartPanes.length) {
      return false;
    }

    return (
      <TooltipContainer
        placement="top-end"
        inPortal
        label={t('chart:fullScreenTooltip')}
        data-testid="tooltip-container"
      >
        <div className={styles.fullscreenButtonContainer}>
          <Button
            variant="empty"
            iconName="Expand"
            size="small"
            onClick={() => onClickFullscreen(chartState)}
            data-testid="open-full-chart"
          />
        </div>
      </TooltipContainer>
    );
  };

  return (
    <div
      data-testid="simple-chart"
      className={clsx(styles.container, {
        [styles.isChatMessage]: isChatMessage,
      })}
    >
      {shouldShowChartHeader && (
        <ChartHeader
          isLegacyVariant={isLegacyVariant}
          isChatMessage={isChatMessage}
          chartData={chartData}
          entity={entity}
          chartConfig={{
            ...chartState,
            ...primaryPane,
          }}
          horizonWithPriceRanges={horizonWithPriceRanges}
          changeSeriesType={changeSeriesType}
          seriesType={chartPanes[0].seriesType}
          renderFullScreenButton={renderFullScreenButton}
          showChartTypeSelector={showChartTypeSelector}
        />
      )}
      <section className={styles.body}>
        <ChartAreaWrapper
          className={styles.styledChartAreaWrapper}
          assetWithError={assetWithError}
          hasError={hasError}
          hasEmpty={hasEmpty}
          colors={colors}
          chartData={chartData}
          chartConfig={finalChartConfig}
          onRetry={retryInitialChartLoad}
          renderChartArea={() =>
            horizonWithPriceRanges.length && primaryAsset && primaryPane ? (
              <ChartAreaV2
                viewControllerClassName={styles.viewController}
                series={chartV2Series}
                chartData={chartData}
                primaryPane={primaryPane}
                horizon={horizon}
                resample={resample}
                availableHorizons={availableHorizons}
                disable1DHorizon={disable1DHorizon}
                insights={chartV2Insights}
                enableZoom={Boolean(finalChartConfig.enableZoom)}
                enableHover={Boolean(finalChartConfig.enableHover)}
                isLegacyVariant={isLegacyVariant}
                topBarActions={renderFullScreenButton()}
                onHorizonChange={onRangeChange}
                changeSeriesType={changeSeriesType}
                domain={chartV2Domain}
                defaultPriceDisplayByPane={chartV2DefaultPriceDisplayByPane}
              />
            ) : null
          }
        />
      </section>
    </div>
  );
};
